import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Tour, TourState, TourFilters } from '../../models/tour';
import * as tourService from '../../services/tourService';

// Initial state
const initialState: TourState = {
  tours: [],
  discords: [],
  rulesets: [],
  participants: {},
  participantStatus: {},
  loading: {
    tours: false,
    discords: false,
    rulesets: false,
    creating: false,
    updating: false,
    deleting: false,
    participants: false,
    joining: false,
    leaving: false,
  },
  error: {
    tours: null,
    discords: null,
    rulesets: null,
    creating: null,
    updating: null,
    deleting: null,
    participants: null,
    joining: null,
    leaving: null,
  },
  filters: {},
  editingTour: null,
};

// Async thunks
export const fetchTours = createAsyncThunk('tours/fetchTours', async (_, { rejectWithValue }) => {
  try {
    const tours = await tourService.getAllTours();
    return tours;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch tours');
  }
});

export const fetchDiscords = createAsyncThunk(
  'tours/fetchDiscords',
  async (_, { rejectWithValue }) => {
    try {
      const discords = await tourService.getAllDiscords();
      return discords;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch Discord teams');
    }
  }
);

export const fetchRulesets = createAsyncThunk(
  'tours/fetchRulesets',
  async (_, { rejectWithValue }) => {
    try {
      const rulesets = await tourService.getAllRulesets();
      return rulesets;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch rulesets');
    }
  }
);

export const createTour = createAsyncThunk(
  'tours/createTour',
  async (tour: Tour, { rejectWithValue }) => {
    try {
      const newTour = await tourService.createTour(tour);
      return newTour;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create tour');
    }
  }
);

export const updateTour = createAsyncThunk(
  'tours/updateTour',
  async ({ id, tour }: { id: string; tour: Tour }, { rejectWithValue }) => {
    try {
      const updatedTour = await tourService.updateTour(id, tour);
      return updatedTour;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update tour');
    }
  }
);

export const deleteTour = createAsyncThunk(
  'tours/deleteTour',
  async (id: string, { rejectWithValue }) => {
    try {
      await tourService.deleteTour(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete tour');
    }
  }
);

export const fetchTourById = createAsyncThunk(
  'tours/fetchTourById',
  async (id: string, { rejectWithValue }) => {
    try {
      const tour = await tourService.getTourById(id);
      return tour;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tour');
    }
  }
);

export const fetchTourParticipants = createAsyncThunk(
  'tours/fetchTourParticipants',
  async (tourId: string, { rejectWithValue }) => {
    try {
      const participants = await tourService.getTourParticipants(tourId);
      return { tourId, participants };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tour participants');
    }
  }
);

export const fetchTourParticipantStatus = createAsyncThunk(
  'tours/fetchTourParticipantStatus',
  async (tourId: string, { rejectWithValue }) => {
    try {
      const status = await tourService.getTourParticipantStatus(tourId);
      return { tourId, status };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch participant status');
    }
  }
);

export const joinTour = createAsyncThunk(
  'tours/joinTour',
  async (tourId: string, { rejectWithValue, dispatch }) => {
    try {
      await tourService.joinTour(tourId);
      // Refresh participant status after joining
      dispatch(fetchTourParticipantStatus(tourId));
      return tourId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to join tour');
    }
  }
);

export const leaveTour = createAsyncThunk(
  'tours/leaveTour',
  async (tourId: string, { rejectWithValue, dispatch }) => {
    try {
      await tourService.leaveTour(tourId);
      // Refresh participant status after leaving
      dispatch(fetchTourParticipantStatus(tourId));
      return tourId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to leave tour');
    }
  }
);

// Slice
const tourSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    setEditingTour: (state, action: PayloadAction<Tour | null>) => {
      state.editingTour = action.payload;
    },
    clearEditingTour: (state) => {
      state.editingTour = null;
    },
    setFilters: (state, action: PayloadAction<TourFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearErrors: (state) => {
      state.error = {
        tours: null,
        discords: null,
        rulesets: null,
        creating: null,
        updating: null,
        deleting: null,
        participants: null,
        joining: null,
        leaving: null,
      };
    },
    clearError: (state, action: PayloadAction<keyof TourState['error']>) => {
      state.error[action.payload] = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tours
    builder
      .addCase(fetchTours.pending, (state) => {
        state.loading.tours = true;
        state.error.tours = null;
      })
      .addCase(fetchTours.fulfilled, (state, action) => {
        state.loading.tours = false;
        state.tours = action.payload || [];
      })
      .addCase(fetchTours.rejected, (state, action) => {
        state.loading.tours = false;
        state.error.tours = action.payload as string;
      });

    // Fetch discords
    builder
      .addCase(fetchDiscords.pending, (state) => {
        state.loading.discords = true;
        state.error.discords = null;
      })
      .addCase(fetchDiscords.fulfilled, (state, action) => {
        state.loading.discords = false;
        state.discords = action.payload || [];
      })
      .addCase(fetchDiscords.rejected, (state, action) => {
        state.loading.discords = false;
        state.error.discords = action.payload as string;
      });

    // Fetch rulesets
    builder
      .addCase(fetchRulesets.pending, (state) => {
        state.loading.rulesets = true;
        state.error.rulesets = null;
      })
      .addCase(fetchRulesets.fulfilled, (state, action) => {
        state.loading.rulesets = false;
        state.rulesets = action.payload || [];
      })
      .addCase(fetchRulesets.rejected, (state, action) => {
        state.loading.rulesets = false;
        state.error.rulesets = action.payload as string;
      });

    // Create tour
    builder
      .addCase(createTour.pending, (state) => {
        state.loading.creating = true;
        state.error.creating = null;
      })
      .addCase(createTour.fulfilled, (state, action) => {
        state.loading.creating = false;
        if (!state.tours) {
          state.tours = [];
        }
        state.tours.push(action.payload);
        state.editingTour = null;
      })
      .addCase(createTour.rejected, (state, action) => {
        state.loading.creating = false;
        state.error.creating = action.payload as string;
      });

    // Update tour
    builder
      .addCase(updateTour.pending, (state) => {
        state.loading.updating = true;
        state.error.updating = null;
      })
      .addCase(updateTour.fulfilled, (state, action) => {
        state.loading.updating = false;
        if (!state.tours) {
          state.tours = [];
        }
        const index = state.tours.findIndex((tour) => tour.id === action.payload.id);
        if (index !== -1) {
          state.tours[index] = action.payload;
        }
        state.editingTour = null;
      })
      .addCase(updateTour.rejected, (state, action) => {
        state.loading.updating = false;
        state.error.updating = action.payload as string;
      });

    // Delete tour
    builder
      .addCase(deleteTour.pending, (state) => {
        state.loading.deleting = true;
        state.error.deleting = null;
      })
      .addCase(deleteTour.fulfilled, (state, action) => {
        state.loading.deleting = false;
        if (!state.tours) {
          state.tours = [];
        } else {
          state.tours = state.tours.filter((tour) => tour.id !== action.payload);
        }
      })
      .addCase(deleteTour.rejected, (state, action) => {
        state.loading.deleting = false;
        state.error.deleting = action.payload as string;
      });

    // Fetch tour by ID
    builder
      .addCase(fetchTourById.pending, (state) => {
        state.loading.tours = true;
        state.error.tours = null;
      })
      .addCase(fetchTourById.fulfilled, (state, action) => {
        state.loading.tours = false;
        if (!state.tours) {
          state.tours = [];
        }
        const existingIndex = state.tours.findIndex((tour) => tour.id === action.payload.id);
        if (existingIndex !== -1) {
          state.tours[existingIndex] = action.payload;
        } else {
          state.tours.push(action.payload);
        }
      })
      .addCase(fetchTourById.rejected, (state, action) => {
        state.loading.tours = false;
        state.error.tours = action.payload as string;
      });

    // Fetch tour participants
    builder
      .addCase(fetchTourParticipants.pending, (state) => {
        state.loading.participants = true;
        state.error.participants = null;
      })
      .addCase(fetchTourParticipants.fulfilled, (state, action) => {
        state.loading.participants = false;
        state.participants[action.payload.tourId] = action.payload.participants;
      })
      .addCase(fetchTourParticipants.rejected, (state, action) => {
        state.loading.participants = false;
        state.error.participants = action.payload as string;
      });

    // Fetch tour participant status
    builder
      .addCase(fetchTourParticipantStatus.pending, (state) => {
        state.loading.participants = true;
        state.error.participants = null;
      })
      .addCase(fetchTourParticipantStatus.fulfilled, (state, action) => {
        state.loading.participants = false;
        state.participantStatus[action.payload.tourId] = action.payload.status;
      })
      .addCase(fetchTourParticipantStatus.rejected, (state, action) => {
        state.loading.participants = false;
        state.error.participants = action.payload as string;
      });

    // Join tour
    builder
      .addCase(joinTour.pending, (state) => {
        state.loading.joining = true;
        state.error.joining = null;
      })
      .addCase(joinTour.fulfilled, (state) => {
        state.loading.joining = false;
      })
      .addCase(joinTour.rejected, (state, action) => {
        state.loading.joining = false;
        state.error.joining = action.payload as string;
      });

    // Leave tour
    builder
      .addCase(leaveTour.pending, (state) => {
        state.loading.leaving = true;
        state.error.leaving = null;
      })
      .addCase(leaveTour.fulfilled, (state) => {
        state.loading.leaving = false;
      })
      .addCase(leaveTour.rejected, (state, action) => {
        state.loading.leaving = false;
        state.error.leaving = action.payload as string;
      });
  },
});

export const {
  setEditingTour,
  clearEditingTour,
  setFilters,
  clearFilters,
  clearErrors,
  clearError,
} = tourSlice.actions;

export default tourSlice.reducer;
